// Third-party packages
import {
	Collection,
} from '@@types/Collection';
import {
	ContactOffer,
	ContactOfferJson,
} from '@@types/ContactOffer';
import {
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';

export interface OfferEntryContactsState {
	contacts: Collection<ContactOfferJson, ContactOffer>;
	isError: boolean;
	isLoading: boolean;
}

export interface OffersEntryContactsStore {
	[offerId: number]: OfferEntryContactsState;
}

const initialState: OffersEntryContactsStore = {};

export const offersEntryContactsSlice = createSlice({
	initialState,
	name: 'offers_entry_contacts',
	reducers: {
		clearOfferEntryContacts: (state, action: PayloadAction<number>) => {
			const offerId = action.payload;
			delete state[offerId];
		},
		deleteOfferEntryContacts: (state, action: PayloadAction<{
			contactIds: number[];
			offerId: number;
		}>) => {
			const {
				contactIds,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				return;
			}
			state[offerId].contacts.collection = (state[offerId].contacts.collection || []).filter(
				(contact) => !contactIds.includes(contact.id as number),
			) as unknown as ContactOfferJson[];
			state[offerId].contacts.count = state[offerId].contacts.collection.length;
		},
		setOfferEntryContacts: (state, action: PayloadAction<{
			contacts: Collection<ContactOfferJson, ContactOffer>;
			offerId: number;
		}>) => {
			const {
				contacts,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					contacts: {
						collection: [],
						count: 0,
					} as Collection<ContactOfferJson, ContactOffer>,
					isError: false,
					isLoading: false,
				};
			}
			state[offerId].contacts = contacts;
		},
		setOfferEntryContactsError: (state, action: PayloadAction<{
			isError: boolean;
			offerId: number;
		}>) => {
			const {
				isError,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					contacts: {
						collection: [],
						count: 0,
					} as Collection<ContactOfferJson, ContactOffer>,
					isError: false,
					isLoading: false,
				};
			}
			state[offerId].isError = isError;
		},
		setOfferEntryContactsLoading: (state, action: PayloadAction<{
			isLoading: boolean;
			offerId: number;
		}>) => {
			const {
				isLoading,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					contacts: {
						collection: [],
						count: 0,
					} as Collection<ContactOfferJson, ContactOffer>,
					isError: false,
					isLoading: false,
				};
			}
			state[offerId].isLoading = isLoading;
		},
		updateOfferEntryContact: (state, action: PayloadAction<{
			contact: Partial<ContactOffer> & { id: number };
			offerId: number;
		}>) => {
			const {
				contact,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				return;
			}
			const updatedCollection = (state[offerId].contacts.collection || []).map((existingContact) =>
				(existingContact?.id as number) === contact.id ? {
					...existingContact,
					...contact,
				} : existingContact,
			);
			state[offerId].contacts.collection = updatedCollection as unknown as ContactOfferJson[];
		},
	},
});

export const {
	clearOfferEntryContacts,
	deleteOfferEntryContacts,
	setOfferEntryContacts,
	setOfferEntryContactsError,
	setOfferEntryContactsLoading,
	updateOfferEntryContact,
} = offersEntryContactsSlice.actions;

export const offersEntryContactsReducer = offersEntryContactsSlice.reducer;


